<script setup lang="ts">
import { computed, ref, type Component } from 'vue'
import type { ChromeLink } from '@/composables/usePageChrome'
import { useProximity } from '@/composables/useProximity'
import AccountGroupIcon from 'vue-material-design-icons/AccountGroup.vue'
import MessageTextOutlineIcon from 'vue-material-design-icons/MessageTextOutline.vue'
import BookOpenVariantIcon from 'vue-material-design-icons/BookOpenVariant.vue'
import AccountOutlineIcon from 'vue-material-design-icons/AccountOutline.vue'
import CreationOutlineIcon from 'vue-material-design-icons/CreationOutline.vue'
import EarthIcon from 'vue-material-design-icons/Earth.vue'

/**
 * The chrome's lateral-links region: a curated row of type-level "sideways"
 * destinations declared by the route (static `meta.links`) or supplied at
 * runtime via `usePageChrome`. Purely presentational — values flow in from the
 * shell.
 *
 * These are real `RouterLink`s (anchors), not ascent and not history: each is a
 * deterministic door to a related *kind*. Home declares the broad section
 * launcher here; most other routes declare none or a single structural sibling.
 * The active sibling (if any) is highlighted via RouterLink's own
 * `router-link-active` class so the row doubles as orientation.
 */
const props = defineProps<{ links: ChromeLink[] }>()

const iconMap: Record<NonNullable<ChromeLink['iconName']>, Component> = {
  characters: AccountGroupIcon,
  conversations: MessageTextOutlineIcon,
  lorebooks: BookOpenVariantIcon,
  worlds: EarthIcon,
  personas: AccountOutlineIcon,
  creator: CreationOutlineIcon,
}

const linksWithIcons = computed(() =>
  props.links.map((link) => ({
    ...link,
    icon: link.iconName ? iconMap[link.iconName] : null,
  })),
)

// The bar "comes alive" as the pointer enters its zone of influence — the full
// viewport width and a 50px band below the row — rather than only on contact,
// so the labels finish revealing as visual attention lands on the bar. Occupancy
// only (it captures no pointer events); `:hover`/`:focus-within` remain the
// baseline triggers below for keyboard, touch, and no-JS.
const navRef = ref<HTMLElement | null>(null)
const { near } = useProximity(navRef, { bottom: 50, fullWidth: true })
</script>

<template>
  <nav
    ref="navRef"
    class="chrome-links"
    :class="{ 'chrome-links--near': near }"
    aria-label="Sections"
  >
    <RouterLink
      v-for="link in linksWithIcons"
      :key="link.label"
      :to="link.to"
      class="chrome-links__item"
      :class="{ 'chrome-links__item--with-icon': link.icon }"
      :aria-label="link.label"
    >
      <component
        :is="link.icon"
        v-if="link.icon"
        class="chrome-links__icon"
        aria-hidden="true"
      />
      <span class="chrome-links__label">{{ link.label }}</span>
    </RouterLink>
  </nav>
</template>

<style scoped>
.chrome-links {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
}

.chrome-links::-webkit-scrollbar {
  display: none;
}

.chrome-links__item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  color: var(--theme-text-secondary);
  text-decoration: none;
  transition: background-color 150ms, color 150ms, gap 180ms ease;
}

.chrome-links__item:hover {
  color: var(--theme-on-surface, rgba(255, 255, 255, 0.9));
  background-color: var(--theme-surface-interactive, rgba(255, 255, 255, 0.07));
}

.chrome-links__item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--theme-focus-ring);
}

/* The currently-active sibling, when one of these links matches the route. */
.chrome-links__item.router-link-active {
  color: var(--theme-on-surface, #ffffff);
  background-color: rgba(29, 138, 255, 0.12);
}

.chrome-links__icon {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.chrome-links__icon :deep(svg) {
  width: 100%;
  height: 100%;
}

.chrome-links__label {
  display: inline-block;
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  transition: max-width 180ms ease, opacity 150ms ease;
}

.chrome-links:hover .chrome-links__item--with-icon,
.chrome-links:focus-within .chrome-links__item--with-icon,
.chrome-links--near .chrome-links__item--with-icon {
  gap: 8px;
}

.chrome-links:hover .chrome-links__item--with-icon .chrome-links__label,
.chrome-links:focus-within .chrome-links__item--with-icon .chrome-links__label,
.chrome-links--near .chrome-links__item--with-icon .chrome-links__label {
  max-width: 128px;
  opacity: 1;
}

</style>
